// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { get } from "../api/api"; // must return a Promise

const AuthContext = createContext();

// Optional: normalize roles to an array of strings (supports {id,name} or strings)
function normalizeRoles(input) {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input
      .map((r) => (typeof r === "string" ? r : r?.name))
      .filter(Boolean);
  }
  // If API returns { roles: [...] }
  if (Array.isArray(input.roles)) return normalizeRoles(input.roles);
  return [];
}

export const AuthProvider = ({ children }) => {
  // lazy init from localStorage (avoids double reads on render)
  const [authToken, setAuthToken] = useState(() =>
    localStorage.getItem("authToken")
  );
  const [roles, setRoles] = useState(() => {
    try {
      const raw = localStorage.getItem("roles");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // keep localStorage in sync — NO network calls here
  useEffect(() => {
    if (authToken) localStorage.setItem("authToken", authToken);
    else localStorage.removeItem("authToken");
  }, [authToken]);

  useEffect(() => {
    localStorage.setItem("roles", JSON.stringify(roles || []));
  }, [roles]);

  // Call this after a successful login
  // If your login API already returns roles, pass them in to skip the /roles call.
  const login = async (token, rolesFromLogin = null) => {
    setAuthToken(token);

    if (rolesFromLogin && rolesFromLogin.length) {
      setRoles(normalizeRoles(rolesFromLogin));
      return;
    }

    // Fetch ONCE right after login if roles weren't provided
    try {
      const data = await get("/roles"); // must use token via your http client
      setRoles(normalizeRoles(data));
    } catch (err) {
      console.error("Failed to load roles:", err);
      setRoles([]); // keep app functional even if roles call fails
    }
  };

  const refreshRoles = async () => {
    if (!authToken) return;
    try {
      const data = await get("/roles");
      setRoles(normalizeRoles(data));
    } catch (err) {
      console.error("Failed to refresh roles:", err);
    }
  };

  const logout = () => {
    setAuthToken(null);
    setRoles([]);
    localStorage.removeItem("authToken");
    localStorage.removeItem("roles");
    localStorage.removeItem("routes");
  };

  const hasRole = (roleName) => {
    const list = normalizeRoles(roles);
    return list.includes(roleName);
  };

  const value = useMemo(
    () => ({
      authToken,
      roles: normalizeRoles(roles),
      login,
      logout,
      hasRole,
      refreshRoles,
    }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authToken, roles]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Tiny helper to consume the context
export const useAuth = () => useContext(AuthContext);
